import {
  COURSE_CREATED,
  DELETE_WEEKLY_CLASS,
  PASSWORD_SET_SUCCESS,
  SET_AUTH_STATUS,
  UPDATE_ACADLY_COURSES,
  UPDATE_ACCESS_DATA,
  UPDATE_ALL_COURSES,
  UPDATE_ALL_FACULTY_ACCOUNTS,
  UPDATE_COURSE_ADMIN,
  UPDATE_COURSE_DATES,
  UPDATE_COURSE_DETAILS,
  UPDATE_COURSE_TIMELINE,
  UPDATE_ENROLLMENTS,
  UPDATE_FACULTY_CACHE_STATUS,
  UPDATE_SIGNEDUP_FACULTY_ACCOUNTS,
  UPDATE_STUDENT_ACCOUNTS,
} from './actions';

export interface RootState {
  isAppLoading: boolean;
  isAuthorized: boolean;
  getAccess: null | SessionData;
  serverError: null | string;
  facultyAccounts: null | {
    signed_up_accounts: SignedUpUser[];
    all_accounts: {
      cache: 'notdone' | 'processing' | 'done';
      users: {
        primaryId: string;
        name: string;
        emailId: string;
        username: string;
      }[];
      imported: number;
      importRemaining: number;
    };
  };
  studentAccounts: SignedUpUser[];
  acadlyCourses: ImportedCourse[];
  allCourses: AvailableCourse[];
  analytics: null;
  courseInCreation: null | CourseDetails;
  timeline: CourseTimelineClass[];
}

const defaultState: RootState = {
  isAppLoading: true,
  isAuthorized: false,
  getAccess: null,
  serverError: null,
  facultyAccounts: null,
  studentAccounts: [],
  acadlyCourses: [],
  allCourses: [],
  analytics: null,
  courseInCreation: null,
  timeline: [],
};

interface Action {
  type: string;
  payload: any;
}

export const reducer = (state: RootState = defaultState, action: Action): RootState => {
  switch (action.type) {
    case SET_AUTH_STATUS: {
      // action payload is a boolean
      return {
        ...state,
        isAuthorized: action.payload,
        getAccess: action.payload ? state.getAccess : null,
      };
    }

    case UPDATE_ACCESS_DATA: {
      return {
        ...state,
        getAccess: action.payload,
        isAppLoading: false,
      };
    }

    case PASSWORD_SET_SUCCESS: {
      if (!state.getAccess) return state;
      return {
        ...state,
        isAuthorized: true,
        getAccess: {
          ...state.getAccess,
          passwordSet: 1,
        },
      };
    }

    case UPDATE_SIGNEDUP_FACULTY_ACCOUNTS: {
      if (!state.facultyAccounts) return state;
      return {
        ...state,
        facultyAccounts: {
          ...state.facultyAccounts,
          signed_up_accounts: action.payload,
        },
      };
    }

    case UPDATE_ALL_FACULTY_ACCOUNTS: {
      if (!state.facultyAccounts) return state;
      return {
        ...state,
        facultyAccounts: {
          ...state.facultyAccounts,
          all_accounts: action.payload,
        },
      };
    }

    case UPDATE_FACULTY_CACHE_STATUS: {
      if (!state.facultyAccounts) return state;
      return {
        ...state,
        facultyAccounts: {
          ...state.facultyAccounts,
          all_accounts: {
            ...state.facultyAccounts.all_accounts,
            cache: action.payload,
          },
        },
      };
    }

    case UPDATE_STUDENT_ACCOUNTS: {
      return {
        ...state,
        studentAccounts: action.payload,
      };
    }

    case UPDATE_ACADLY_COURSES: {
      return {
        ...state,
        acadlyCourses: action.payload,
      };
    }

    case UPDATE_ALL_COURSES: {
      return {
        ...state,
        allCourses: action.payload,
      };
    }

    case UPDATE_COURSE_DETAILS: {
      return {
        ...state,
        courseInCreation: action.payload,
      };
    }

    case UPDATE_COURSE_ADMIN: {
      // INFO: action.payload is admin userId
      let newInstructors: CourseUser[] = [];

      if (!state.courseInCreation) return state;

      const newAdmin: CourseUser | undefined = state.courseInCreation.team.instructors.find(
        (ins) => ins.userId === action.payload
      );

      if (state.courseInCreation.team.isAdminSet) {
        newInstructors = [
          state.courseInCreation.team.admin,
          ...state.courseInCreation.team.instructors.filter(
            (ins) => ins.userId && ins.userId !== action.payload
          ),
        ];
      } else {
        newInstructors = [
          ...state.courseInCreation.team.instructors.filter(
            (ins) => ins.userId && ins.userId !== action.payload
          ),
        ];
      }

      return {
        ...state,
        courseInCreation: {
          ...state.courseInCreation,
          team: newAdmin
            ? {
                ...state.courseInCreation.team,
                admin: newAdmin,
                isAdminSet: 1,
                instructors: newInstructors,
              }
            : {
                ...state.courseInCreation.team,
                admin: {},
                isAdminSet: 0,
                instructors: newInstructors,
              },
        },
      };
    }

    case UPDATE_COURSE_DATES: {
      if (!state.courseInCreation) return state;
      return {
        ...state,
        courseInCreation: {
          ...state.courseInCreation,
          dates: {
            ...state.courseInCreation.dates,
            ...action.payload,
          },
        },
      };
    }

    case DELETE_WEEKLY_CLASS: {
      // INFO: action.payload = ${day}${startTime}${endTime}
      if (!state.courseInCreation) return state;
      const newSchedule = state.courseInCreation.dates.schedule.filter(
        (sc) => action.payload !== sc.day + sc.startTime + sc.endTime
      );
      return {
        ...state,
        courseInCreation: {
          ...state.courseInCreation,
          dates: {
            ...state.courseInCreation.dates,
            schedule: newSchedule,
          },
        },
      };
    }

    case UPDATE_COURSE_TIMELINE: {
      return {
        ...state,
        timeline: action.payload as CourseTimelineClass[],
      };
    }

    case UPDATE_ENROLLMENTS: {
      if (!state.courseInCreation) return state;
      return {
        ...state,
        courseInCreation: {
          ...state.courseInCreation,
          enrolments: action.payload,
        },
      };
    }

    case COURSE_CREATED: {
      return {
        ...state,
        courseInCreation: null,
        allCourses: state.allCourses.map((c) => {
          if (c.courseId === action.payload) {
            c.isImported = 1;
          }
          return c;
        }),
      };
    }

    default:
      return state;
  }
};
